import {
	type FuturesMarketAsset,
	type FuturesVolumes,
	type PerpsMarketV2,
	type PerpsMarketV3,
	PerpsProvider,
} from '@kwenta/sdk/types'
import { wei } from '@kwenta/wei'
import { useMemo } from 'react'

import { selectMarketsByProvider } from 'state/futures/common/selectors'
import { selectOptimismMarkPrices } from 'state/futures/isolatedMargin/selectors'
import { selectMarketVolumesByProvider } from 'state/futures/selectors'
import { selectMarkPricesV3ByProvider } from 'state/futures/snxPerpsV3/selectors'
import { useAppSelector } from 'state/hooks'

const calculateTotalOpenInterest = (
	markets: PerpsMarketV2<string>[] | PerpsMarketV3<string>[],
	prices: Partial<Record<FuturesMarketAsset, string>>
) => {
	return markets.reduce((total, { asset, openInterest }) => {
		const oi = wei(openInterest.long)
			.add(wei(openInterest.short))
			.mul(wei(prices[asset] ?? 0))
			.toNumber()
		return total + oi
	}, 0)
}

const getTotalDailyVolume = (volumes: FuturesVolumes<string>, key: string) =>
	volumes[key]?.volume || 0

const useProvidersStats = () => {
	const markets = useAppSelector(selectMarketsByProvider)

	const v2OptimismPrices = useAppSelector(selectOptimismMarkPrices)
	const v3Prices = useAppSelector(selectMarkPricesV3ByProvider)
	const volumes = useAppSelector(selectMarketVolumesByProvider)

	const data = useMemo(() => {
		return {
			totalDailyVolume: {
				[PerpsProvider.SNX_V2_OP]: getTotalDailyVolume(volumes.snx_v2_op ?? {}, '0x'),
				[PerpsProvider.SNX_V3_BASE]: getTotalDailyVolume(volumes.snx_v3_base ?? {}, 'ALL'),
			},
			totalOI: {
				[PerpsProvider.SNX_V2_OP]: calculateTotalOpenInterest(markets.snx_v2_op, v2OptimismPrices),
				[PerpsProvider.SNX_V3_BASE]: calculateTotalOpenInterest(
					markets.snx_v3_base,
					v3Prices.snx_v3_base
				),
			},
		}
	}, [
		markets.snx_v2_op,
		markets.snx_v3_base,
		v2OptimismPrices,
		v3Prices,
		volumes.snx_v2_op,
		volumes.snx_v3_base,
	])

	return data
}

export default useProvidersStats
